import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { IconLayoutKanban, IconPlus, IconRestore } from "@tabler/icons-react"
import { useState } from "react"
import { BoardNavigator } from "./components/BoardNavigator"
import { NewBoardDialog } from "./components/NewBoardDialog"
import type { BoardRow, Navigation } from "./types"

interface Props {
  [key: string]: unknown
  navigation: Navigation
  recents: BoardRow[]
  archived: BoardRow[]
  can_create: boolean
  projects: { id: number; name: string }[]
}

export default function BoardsIndex() {
  const { navigation, recents, archived, can_create, projects } = usePage<Props>().props
  const [creating, setCreating] = useState(false)

  return (
    <>
      <Head title="Boards" />

      <div className="flex flex-1">
        <BoardNavigator navigation={navigation} />

        <main className="min-w-0 flex-1 px-8 py-10">
          <div className="mx-auto max-w-3xl space-y-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h1 className="text-2xl font-semibold">Boards</h1>
                <p className="text-muted-foreground text-sm">
                  Company boards everyone can see, and private boards only the people you add can open.
                </p>
              </div>

              {can_create && (
                <Button onClick={() => setCreating(true)}>
                  <IconPlus className="size-4" />
                  New board
                </Button>
              )}
            </div>

            {recents.length === 0 && archived.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center">
                  <IconLayoutKanban className="text-muted-foreground mx-auto mb-3 size-8" />
                  <p className="font-medium">No boards yet</p>
                  <p className="text-muted-foreground mb-4 text-sm">
                    A board gives its tickets a key, so work becomes something you can name.
                  </p>
                  {can_create && (
                    <Button onClick={() => setCreating(true)}>
                      <IconPlus className="size-4" />
                      Create your first board
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {recents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recently updated</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {recents.map((board) => (
                    <Link
                      key={board.key}
                      href={`/boards/${board.key}`}
                      className="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                    >
                      <IconLayoutKanban className="text-muted-foreground size-4 shrink-0" />
                      <span className="truncate">{board.name}</span>
                      <span className="text-muted-foreground ml-auto shrink-0 text-xs">{board.ticket_count} open</span>
                      <span className="text-muted-foreground shrink-0 font-mono text-[10px]">{board.key}</span>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {archived.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Archived</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {archived.map((board) => (
                    <div key={board.key} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                      <IconLayoutKanban className="text-muted-foreground size-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{board.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Restore ${board.name}`}
                        onClick={() => router.patch(`/boards/${board.key}/restore`)}
                      >
                        <IconRestore className="size-4" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>

      <NewBoardDialog open={creating} onOpenChange={setCreating} projects={projects} />
    </>
  )
}

BoardsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
